import { styled } from '@mui/material/styles';
import { FC, useState } from 'react';

import Table from 'further-ui/components/Table';
import TabsPanel from 'further-ui/components/TabsPanel';

import { Button, Typography } from '@mui/material';
import Chip from 'components/Chip';
import TransactionAllocator from 'components/TransactionAllocator';
import { useLayoutContext } from 'contexts/LayoutContext';
import { ActionOperationType } from 'further-types/lp/action';
import { TransactionOperation } from 'further-types/lp/transaction';
import { usePagination } from 'further-ui/hooks';
import { ActionRefLabels } from 'further-ui/labels';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import useTransactionTypeSelectorOptions from 'hooks/form/useTransactionTypeSelectorOptions';
import useVehicle from 'hooks/lp/data/vehicle/useVehicle';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api from 'lib/trpcClient';
import { useLocation, useParams } from 'react-router-dom';
import { createLpPeriodEndReviewDetailRoute } from 'adminConstants/routes';
import ProcessingPage from 'components/new/ProcessingPage';
import FullPageDrawer from 'components/new/FullPageDrawer';
import { Column, Row } from 'further-ui/components/Layout';

enum ApprovePrepaymentsTab {
  ResolvedPayments = 'resolvedPayments',
  ResolvedDeferredIncome = 'resolvedDeferredIncome',
}

const tabsData = {
  [ApprovePrepaymentsTab.ResolvedPayments]: { title: 'Resolved payments' },
  [ApprovePrepaymentsTab.ResolvedDeferredIncome]: {
    title: 'Resolved deferred income',
  },
};

const TabLabelWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

const ExpandedRowContainer = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  borderTop: `1px solid ${theme.palette.grey[300]}`,
}));

const pageId = 'approve-prepayments';

type Props = {
  onExit: () => void;
};

const ApprovePrepayments: FC<Props> = ({ onExit }) => {
  const { selectedVehicleId } = useLayoutContext();
  const { vehicle } = useVehicle(selectedVehicleId);
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const periodStartParam = searchParams.get('periodStart');
  const periodEndParam = searchParams.get('periodEnd');
  const { periodId } = useParams<{ periodId?: string }>();
  const periodStart = periodStartParam
    ? new CalendarDay(periodStartParam)
    : null;
  const periodEnd = periodEndParam ? new CalendarDay(periodEndParam) : null;
  const [isProcessingApproval, setIsProcessingApproval] = useState(false);
  const [activeTab, setActiveTab] = useState<ApprovePrepaymentsTab>(
    ApprovePrepaymentsTab.ResolvedPayments,
  );
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [transactionChanges, setTransactionChanges] = useState<
    Map<
      string,
      Array<{
        transactionTypeId: string;
        operationType: TransactionOperation;
      }>
    >
  >(new Map());

  const pagination = usePagination({
    id: pageId,
    order: 'desc',
    orderBy: 'ledgerDate',
  });

  const { transactionTypeOptions, isLoading: transactionTypesLoading } =
    useTransactionTypeSelectorOptions();

  const handleMutation = useHandleTrpcMutation<string>();
  const confirmChecklistItems =
    api.accountingPeriod.confirmChecklistItems.useMutation(
      handleMutation({
        refetchType: 'active',
        successRedirect: createLpPeriodEndReviewDetailRoute(periodId ?? ''),
        onError: () => setIsProcessingApproval(false),
      }),
    );

  const handleExit = () => {
    onExit();
  };

  const handleApprove = async () => {
    if (!selectedVehicleId || !periodId) return;
    setIsProcessingApproval(true);

    try {
      const allItems = [
        ...(resolvedPaymentsData?.results ?? []),
        ...(resolvedDeferredIncomeData?.results ?? []),
      ];
      const unapproved = allItems.filter((r) => !r.isAccountScheduleAuthorised);

      if (unapproved.length > 0) {
        await approvePrepayments({
          vehicleId: selectedVehicleId,
          prepayments: unapproved.map((r) => ({
            actionId: r.id,
            transactions: transactionChanges.get(r.id) ?? [],
          })),
        });
      }

      confirmChecklistItems.mutate({
        vehicleId: selectedVehicleId,
        id: periodId,
        items: ['prepaymentResolutions'],
        confirmed: true,
      });
    } catch {
      setIsProcessingApproval(false);
    }
  };

  const queryEnabled = !!periodStart && !!periodEnd;
  const periodParams = {
    vehicleId: selectedVehicleId ?? '',
    periodStart: periodStart ?? undefined,
    periodEnd: periodEnd ?? undefined,
    page: pagination.page,
    rowsPerPage: pagination.rowsPerPage,
    order: pagination.order,
    orderBy: pagination.orderBy,
  };

  const { data: resolvedPaymentsData } =
    api.prepayments.listPrepayments.useQuery(
      { ...periodParams, actionOperationType: ActionOperationType.Spent },
      { enabled: queryEnabled },
    );

  const { data: resolvedDeferredIncomeData } =
    api.prepayments.listPrepayments.useQuery(
      { ...periodParams, actionOperationType: ActionOperationType.Received },
      { enabled: queryEnabled },
    );

  const tabData = {
    [ApprovePrepaymentsTab.ResolvedPayments]: resolvedPaymentsData,
    [ApprovePrepaymentsTab.ResolvedDeferredIncome]: resolvedDeferredIncomeData,
  };

  const tabCounts = {
    [ApprovePrepaymentsTab.ResolvedPayments]:
      resolvedPaymentsData?.totalCount ?? 0,
    [ApprovePrepaymentsTab.ResolvedDeferredIncome]:
      resolvedDeferredIncomeData?.totalCount ?? 0,
  };

  const data = tabData[activeTab];

  const handleTabChange = (tab: ApprovePrepaymentsTab) => {
    setExpandedRowId(null);
    setActiveTab(tab);
    pagination.handleChangeSort('ledgerDate', 'desc');
  };

  const handleTransactionChange = (
    rowId: string,
    newTransactions: Array<{
      transactionTypeId: string;
      operationType: TransactionOperation;
    }>,
  ) => {
    setTransactionChanges((prev) => {
      const newMap = new Map(prev);
      newMap.set(rowId, newTransactions);
      return newMap;
    });
  };

  const mutationOpts = useHandleTrpcMutation();
  const { mutateAsync: approvePrepayments } =
    api.prepayments.approvePrepayments.useMutation(
      mutationOpts({
        successMessage: 'Prepayments approved successfully.',
      }),
    );

  const rowsForTable = (data?.results ?? []).map((row) => ({
    ...row,
    id: row.id ?? '',
  }));

  const columns = [
    {
      key: 'actionRef',
      label: 'Action',
      render: (row) => ActionRefLabels[row.actionRef],
      disableSorting: true,
    },
    {
      key: 'description',
      label: 'Description',
      disableSorting: true,
    },
    {
      key: 'ledgerDate',
      label: 'Date',
      render: (row) => row.ledgerDate.toLabel(),
    },
    {
      key: 'amountTcy',
      label: 'Amount (TCY)',
      render: (row) => numberToCurrencyString(row.amountTcy, { fallback: '-' }),
      disableSorting: true,
    },
    {
      key: 'amountLcy',
      label: 'Amount (LCY)',
      render: (row) => numberToCurrencyString(row.amountLcy, { fallback: '-' }),
      disableSorting: true,
    },
    {
      key: 'counterpartyLabel',
      label: 'Counterparty',
      disableSorting: true,
    },
    {
      key: 'isAccountScheduleAuthorised',
      label: 'Approved',
      render: (row) => (row.isAccountScheduleAuthorised ? 'Yes' : 'No'),
      disableSorting: true,
    },
  ];

  return (
    <FullPageDrawer
      open
      size="lg"
      innerScroll
      onClose={handleExit}
      title="Automated Transactions: Resolve prepayments"
      actions={
        <Row fullWidth justify="space-between">
          <Button size="large" variant="outlined" onClick={handleExit}>
            Exit
          </Button>
          {periodId && (
            <Button size="large" variant="contained" onClick={handleApprove}>
              Approve and mark as reviewed
            </Button>
          )}
        </Row>
      }
    >
      <Column spacing="md" fullWidth>
        {isProcessingApproval ? (
          <ProcessingPage
            title="Approving transactions"
            subtitle="This will only take a moment."
          />
        ) : (
          <>
            <Typography variant="h5">Resolve prepayments</Typography>
            <Typography variant="body2">
              Review and approve prepayment resolutions from{' '}
              {periodStart?.toLabel()} – {periodEnd?.toLabel()}.
            </Typography>
            <TabsPanel
              tabs={Object.entries(tabsData).map(([tab, { title }]) => ({
                key: tab as ApprovePrepaymentsTab,
                label: (
                  <TabLabelWrapper>
                    {title}
                    <Chip variant="tabIndicator" small bold={false}>
                      {tabCounts[tab as ApprovePrepaymentsTab]}
                    </Chip>
                  </TabLabelWrapper>
                ),
                content: (
                  <>
                    <Table
                      columns={columns}
                      rows={rowsForTable}
                      pagination={{
                        page: pagination.page,
                        rowsPerPage: pagination.rowsPerPage,
                        order: pagination.order,
                        orderBy: pagination.orderBy,
                        handleRequestSort: pagination.handleRequestSort,
                        handleChangePage: pagination.handleChangePage,
                        handleChangeRowsPerPage:
                          pagination.handleChangeRowsPerPage,
                      }}
                      rowsTotalCount={data?.totalCount ?? 0}
                      isExpandableRow={() => true}
                      isExpandedRow={(row) => expandedRowId === row.id}
                      onRowExpand={(row) =>
                        setExpandedRowId((prev) =>
                          prev === row.id ? null : row.id,
                        )
                      }
                      getExpandedContent={(row) => (
                        <ExpandedRowContainer>
                          {!transactionTypesLoading && vehicle && (
                            <TransactionAllocator
                              showCurrencyRate
                              transactionTypeOptions={transactionTypeOptions}
                              transactions={row.transactions ?? []}
                              readOnly={row.isAccountScheduleAuthorised}
                              onChange={(transactions) => {
                                handleTransactionChange(
                                  row.id ?? '',
                                  transactions,
                                );
                              }}
                              transactionCurrency={row.currencyCode}
                              vehicle={vehicle}
                              fxRate={null}
                              darkBackground
                            />
                          )}
                        </ExpandedRowContainer>
                      )}
                    />
                  </>
                ),
              }))}
              onTabChange={handleTabChange}
              defaultTab={ApprovePrepaymentsTab.ResolvedPayments}
            />
          </>
        )}
      </Column>
    </FullPageDrawer>
  );
};

export default ApprovePrepayments;
